import { ValidationError } from "@core/domain/errors/validation.error";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { QualityCertificateDatasource } from "@entity/data/datasources/quality-certificate.datasource";
import { EntityQualityCertificateError } from "@entity/domain/errors/entity.error";
import { QualityCertificateMapperError } from "@entity/domain/errors/quality-certificate/quality-certificate-mapper.error";
import { QualityCertificateType } from "@entity/domain/models/quality-certificate-type.model";
import { CreateQualityCertificate } from "@entity/domain/models/quality-framework/quality-certificate/create-quality-certificate.model";
import { EditQualityCertificate } from "@entity/domain/models/quality-framework/quality-certificate/edit-quality-certificate.model";
import {
    QualityCertificate,
    QualityCertificates,
} from "@entity/domain/models/quality-framework/quality-certificate/quality-certificate.model";
import { inject, injectable } from "inversify";

@injectable()
export class QualityCertificateRepository {
    constructor(
        @inject(QualityCertificateDatasource)
        private readonly qualityCertificateDatasource: QualityCertificateDatasource,
    ) { }

    async getAll(
        pagination: Pagination,
    ): Promise<Either<EntityQualityCertificateError, QualityCertificates>> {
        const qualityCertificateResult =
            await this.qualityCertificateDatasource.fetchAll(pagination);

        return qualityCertificateResult.mapLeft(() => new EntityQualityCertificateError("fetchQualityCertificatesError"));
    }

    async create(
        qualityCertificate: CreateQualityCertificate,
    ): Promise<Either<ValidationError | EntityQualityCertificateError | QualityCertificateMapperError, QualityCertificate>> {
        return this.qualityCertificateDatasource.create(qualityCertificate);
    }

    async edit(
        editQualityCertificate: EditQualityCertificate,
    ): Promise<Either<ValidationError | EntityQualityCertificateError | QualityCertificateMapperError, QualityCertificate>> {
        return this.qualityCertificateDatasource.edit(editQualityCertificate);
    }

    async delete(
        qualityCertificateId: number,
    ): Promise<Either<EntityQualityCertificateError, true>> {
        const qualityCertificateResult =
            await this.qualityCertificateDatasource.delete(
                qualityCertificateId,
            );
        return qualityCertificateResult.mapLeft(() => new EntityQualityCertificateError("deleteQualityCertificateError"));
    }

    async getQualityCertificateTypes(): Promise<
        Either<EntityQualityCertificateError, QualityCertificateType[]>
    > {
        return this.qualityCertificateDatasource.fetchQualityCertificateTypes();
    }
}
